import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as client from 'api/authClient';
import { AuthState, LoginValues } from './authModels';

// Initial state
const initialState: AuthState = {
  email: '',
  username: '',
  loggedIn: false,
  status: 'idle',
  error: null,
  token: null,
};

// Async actions
export const verifyUser = createAsyncThunk('Auth/verifyUser', async () => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No access token');
  const response = await client.fetchMe();
  return response.data;
});

export const authSignUp = createAsyncThunk(
  'Auth/signUp',
  async (values: any) => {
    const response = await client.signUp(values);
    if (!response.data.success)
      throw new Error(response.data?.msg || 'Signup failed');
    return response.data;
  },
);

export const authGoogleLogin = createAsyncThunk(
  'Auth/googleLogin',
  async (tokens: { accessToken: string; refreshToken: string }) => {
    localStorage.setItem('token', tokens.accessToken);
    if (tokens.refreshToken)
      localStorage.setItem('refreshToken', tokens.refreshToken);
    const response = await client.fetchMe();
    return response.data;
  },
);

export const authLogin = createAsyncThunk(
  'Auth/login',
  async (values: LoginValues | null) => {
    if (!values) throw new Error('Missing login values');
    const model = {
      Email: values.email,
      Password: values.password,
    };
    const response = await client.login(model);
    const { accessToken, refreshToken } = response.data;
    if (!accessToken) throw new Error('Login failed');
    localStorage.setItem('token', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    const meResponse = await client.fetchMe();
    return meResponse.data;
  },
);

export const authLogout = createAsyncThunk('Auth/logout', async () => {
  localStorage.clear();
});

// Slice
const authSlice = createSlice({
  name: 'Auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<{ email: string; username: string }>) {
      state.email = action.payload.email;
      state.username = action.payload.username;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authLogin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(authLogin.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loggedIn = true;
        state.username = action.payload.Username;
        state.email = action.payload.Email;
        state.token = localStorage.getItem('token');
        state.error = null;
      })
      .addCase(authLogin.rejected, (state, action) => {
        state.status = 'failed';
        state.loggedIn = false;
        state.error = action.error.message || 'Login failed';
      })

      .addCase(authSignUp.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(authSignUp.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(authSignUp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Signup failed';
      })

      .addCase(verifyUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loggedIn = true;
        state.username = action.payload.Username;
        state.email = action.payload.Email;
        state.token = localStorage.getItem('token');
        state.error = null;
      })
      .addCase(verifyUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Verification failed';
      })

      .addCase(authGoogleLogin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(authGoogleLogin.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loggedIn = true;
        state.username = action.payload.Username;
        state.email = action.payload.Email;
        state.token = localStorage.getItem('token');
        state.error = null;
      })
      .addCase(authGoogleLogin.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Google login failed';
      })

      .addCase(authLogout.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(authLogout.fulfilled, (state) => {
        state.status = 'succeeded';
        state.loggedIn = false;
        state.username = '';
        state.email = '';
        state.token = null;
        state.error = null;
      })
      .addCase(authLogout.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Logout failed';
      });
  },
});

export const { setUser } = authSlice.actions;
export default authSlice.reducer;
